import { useState, useEffect } from "react";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import { F, th as palette, Btn } from "@/lib/bricks";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import TrustLedger from "@/components/accounting/TrustLedger";
import BooksSync from "@/components/accounting/BooksSync";
import { booksAction, booksStats, bannerFor } from "@/components/accounting/books";
import { useSearchParams } from "react-router-dom";
import { Page, PageHeader, Stats, StatusTabs, Meta, figLabel, List, Row, Empty, Section, TextLink, Loading, FieldLabel, humanize } from "@/components/calm/Calm";
import { visibleBooks } from '@/lib/integrations';

function fmtCAD(val) {
  if (!val && val !== 0) return '—';
  return new Intl.NumberFormat('en-CA', { style:'currency', currency:'CAD', maximumFractionDigits:0 }).format(val);
}
function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-CA', { year:'numeric', month:'short', day:'numeric' });
}


export default function AccountingPage() {
  const { resolvedTheme } = useTheme();
  const { user, session } = useAuth();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);
  const isManager = user?.role === 'manager' || user?.role === 'admin';

  const [params, setParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(params.get('books') ? 'Books' : 'Summary');
  const [books, setBooks] = useState(null);
  const [mapOpen, setMapOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [plans, setPlans] = useState([]);
  const [trust, setTrust] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [agents, setAgents] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignDraft, setAssignDraft] = useState({}); // agent_id -> { plan_id, effective_from }
  const [busy, setBusy] = useState('');
  const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
  const todayISO = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
  const [stmtForm, setStmtForm] = useState({ agent_id:'', from: monthStart(), to: todayISO() });
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showNewPlan, setShowNewPlan] = useState(false);
  const [planForm, setPlanForm] = useState({ name:'', type:'percentage', rate:'', description:'' });

  const BASE = process.env.REACT_APP_BACKEND_URL;
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` };

  const asList = (d) => Array.isArray(d) ? d : [];
  const load = () => {
    Promise.all([
      fetch(`${BASE}/api/accounting/plans`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/accounting/trust`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/accounting/commissions`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/accounting/summary`, { headers }).then(r => r.json()).catch(() => null),
      fetch(`${BASE}/api/agents`, { headers }).then(r => r.json()).catch(() => []),
      fetch(`${BASE}/api/accounting/plan-assignments`, { headers }).then(r => r.json()).catch(() => []),
    ]).then(([p2, t, c, s, a, pa]) => {
      setPlans(asList(p2));
      setTrust(asList(t));
      setCommissions(asList(c));
      setSummary(s && typeof s === 'object' && !Array.isArray(s) && !s.detail ? s : null);
      setAgents(asList(a));
      setAssignments(asList(pa));
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Books: QuickBooks Online sync (providers in ENABLED_ACCOUNTING_PROVIDERS). Only shown when the office has it switched on.
  const loadBooks = async () => {
    const on = await fetch(`${BASE}/api/accounting/sync/enabled`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null);
    if (!on?.enabled) { setBooks(null); return; }
    const d = await fetch(`${BASE}/api/accounting/sync`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null);
    setBooks(visibleBooks(d));
  };
  useEffect(() => { if (isManager) loadBooks(); }, [isManager]); // eslint-disable-line
  useEffect(() => {
    const msg = bannerFor(params.get('books'));
    if (!msg) return;
    (params.get('books') === 'connected' ? toast.success : toast.error)(msg);
    params.delete('books'); setParams(params, { replace: true });
  }, []); // eslint-disable-line
  const callBooks = async (method, path, body) => {
    const res = await fetch(`${BASE}/api/accounting/sync/${path}`, { method, headers, body: body ? JSON.stringify(body) : undefined }).catch(() => null);
    const d = res ? await res.json().catch(() => ({})) : {};
    if (!res || !res.ok) { toast.error(typeof d?.detail?.message === 'string' ? d.detail.message : errText(d, "That didn't work. Try again.")); return null; }
    if (method === 'GET') return d;
    if (d.url) { window.location.assign(d.url); return d; }
    if (d.connection !== undefined || d.rows) setBooks(prev => visibleBooks({ ...prev, ...d }));
    else await loadBooks();
    if (path === 'connect' && method === 'POST') { toast.success('Connected. Choose your accounts next.'); setMapOpen(true); }
    if (path === 'mapping') toast.success('Accounts saved');
    if (path === 'run' && d.result) {
      const { posted = 0, failed = 0 } = d.result;
      failed ? toast.error(`${posted} posted, ${failed} didn't post`) : toast.success(`${posted} entr${posted === 1 ? 'y' : 'ies'} posted to your books`);
    }
    return d;
  };
  const booksNext = books ? booksAction(books) : null;
  const doBooksAction = async () => {
    if (!booksNext) return;
    if (booksNext.kind === 'map') return setMapOpen(true);
    if (booksNext.kind === 'reconnect') return callBooks('POST', 'connect', { provider: books.connection.provider });
    setSyncing(true); try { await callBooks('POST', 'run', {}); } finally { setSyncing(false); }
  };

  const handleCreatePlan = async () => {
    if (!planForm.name || !planForm.rate) return toast.error("Name and rate required");
    try {
      const res = await fetch(`${BASE}/api/accounting/plans`, { method: 'POST', headers, body: JSON.stringify(planForm) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === 'string' ? d.detail : 'Failed to create plan');
      toast.success("Commission plan created");
      setShowNewPlan(false);
      setPlanForm({ name:'', type:'percentage', rate:'', description:'' });
      load();
    } catch (e) { toast.error(e.message || "Failed to create plan"); }
  };

  const handleCommAction = async (id, action) => {
    try {
      await fetch(`${BASE}/api/accounting/commissions/${id}/${action}`, { method: 'POST', headers });
      toast.success(action === 'approve' ? "Commission approved" : "Commission marked as paid");
      load();
    } catch { toast.error("Action failed"); }
  };

  const errText = (d, fallback) => (typeof d?.detail === 'string' ? d.detail : fallback);

  const handleAssign = async (agentId) => {
    const draft = assignDraft[agentId] || {};
    if (!draft.plan_id) return toast.error("Choose a plan first");
    setBusy(`assign-${agentId}`);
    try {
      const body = { agent_id: agentId };
      if (draft.effective_from) body.effective_from = draft.effective_from;
      const res = await fetch(`${BASE}/api/accounting/plans/${draft.plan_id}/assign`, { method: 'POST', headers, body: JSON.stringify(body) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errText(d, 'Could not assign plan'));
      toast.success(`${d.agent_name || 'Agent'} is on ${d.plan_name} from ${d.effective_from}`);
      setAssignDraft(prev => ({ ...prev, [agentId]: {} }));
      load();
    } catch (e) { toast.error(e.message || 'Could not assign plan'); }
    setBusy('');
  };

  const handleMakeDefault = async (planId) => {
    setBusy(`default-${planId}`);
    try {
      const res = await fetch(`${BASE}/api/accounting/plans/${planId}/make-default`, { method: 'POST', headers });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errText(d, 'Could not change the default plan'));
      toast.success(`${d.name} is now the office default`);
      load();
    } catch (e) { toast.error(e.message || 'Could not change the default plan'); }
    setBusy('');
  };

  const stmtQuery = () => `from=${encodeURIComponent(stmtForm.from || '')}&to=${encodeURIComponent(stmtForm.to || '')}`;
  const stmtAgentId = isManager ? stmtForm.agent_id : (stmtForm.agent_id || user?.id || user?.user_id);

  const handleViewStatement = async () => {
    if (!stmtAgentId) return toast.error("Choose an agent");
    setBusy('stmt');
    try {
      const res = await fetch(`${BASE}/api/accounting/agents/${stmtAgentId}/statement?${stmtQuery()}`, { headers });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(errText(d, 'Could not load statement'));
      setStatement(d);
    } catch (e) { toast.error(e.message || 'Could not load statement'); setStatement(null); }
    setBusy('');
  };

  const handleStatementPdf = async () => {
    if (!stmtAgentId) return toast.error("Choose an agent");
    setBusy('pdf');
    try {
      const res = await fetch(`${BASE}/api/accounting/agents/${stmtAgentId}/statement.pdf?${stmtQuery()}`, { headers: { Authorization: headers.Authorization } });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(errText(d, 'Could not build PDF'));
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = m ? m[1] : 'commission-statement.pdf';
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) { toast.error(e.message || 'Could not build PDF'); }
    setBusy('');
  };

  const currentByAgent = {};
  assignments.forEach(r => { if (r.is_current) currentByAgent[r.agent_id] = r; });
  const defaultPlan = plans.find(pl => pl.is_default);
  const historyCount = (agentId) => assignments.filter(r => r.agent_id === agentId).length;
  const officeAgents = isManager ? agents : agents.filter(a => a.agent_id === (user?.id || user?.user_id));
  const statementAgents = isManager
    ? [{ agent_id: user?.id || user?.user_id, name: `${user?.name || 'You'} (you)` }, ...agents]
    : [{ agent_id: user?.id || user?.user_id, name: user?.name || 'You' }];
  const fieldStyle = { padding:'8px 10px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:13, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none', boxSizing:'border-box' };

  // Only broker-of-record-approved entries count toward the trust balance.
  const trustApproved = trust.filter(e => (e.status || 'approved') === 'approved');
  const trustBalance = trustApproved.reduce((sum, e) => {
    const amt = e.amount_cents != null ? e.amount_cents / 100 : (e.amount || 0);
    const inflow = e.direction ? e.direction === 'in' : e.type === 'deposit';
    return inflow ? sum + amt : sum - amt;
  }, 0);

  const TABS = [['Summary', 'Summary'], ['Commission Plans', 'Plans'], ['Trust Account', 'Trust'], ['Commission Records', 'Records'], ...(books ? [['Books', 'Books']] : [])];
  const link = { background:'none', border:'none', padding:'0 6px', minHeight:44, minWidth:44, cursor:'pointer', fontSize:13, color:p.ink, fontFamily:F.display, textDecoration:'underline', textUnderlineOffset:3, textDecorationColor:p.hairS, whiteSpace:'nowrap' };
  const money = (v) => <span style={{ fontFamily:F.mono, fontSize:12.5 }}>{fmtCAD(v)}</span>;
  const dirIn = (e) => (e.direction || (e.type === 'deposit' ? 'in' : 'out')) === 'in';

  const pendingCount = commissions.filter(c => (c.status || 'pending') === 'pending').length;
  // One main action per tab. The trust ledger carries its own (New entry).
  const mainAction = () => {
    if (activeTab === 'Books') return booksNext ? <Btn variant="primary" dark={dark} data-testid="books-next" disabled={syncing} onClick={doBooksAction}>{syncing ? 'Syncing…' : booksNext.label}</Btn> : null;
    if (activeTab === 'Trust Account' || !isManager) return null;
    if (activeTab === 'Commission Plans') return <Btn dark={dark} data-testid="accounting-new-plan" onClick={() => setShowNewPlan(true)}>New plan</Btn>;
    if (pendingCount > 0) return <Btn dark={dark} data-testid="accounting-review" onClick={() => setActiveTab('Commission Records')}>{activeTab === 'Commission Records' ? `${pendingCount} to approve` : 'Review pending commissions'}</Btn>;
    if (plans.length === 0) return <Btn dark={dark} data-testid="accounting-new-plan" onClick={() => { setActiveTab('Commission Plans'); setShowNewPlan(true); }}>Create a commission plan</Btn>;
    return <Btn dark={dark} data-testid="accounting-statements" onClick={() => setActiveTab('Commission Plans')}>Agent statements</Btn>;
  };

  if (loading) return <Loading />;

  return (
    <Page testid="accounting-page" width={1040}>
      <PageHeader title="Accounting" subtitle="Commissions, agent splits and your Ontario trust account."
        action={mainAction()} />

      <StatusTabs label="Section" options={TABS.map(([key, label]) => ({ key, label }))} value={activeTab} onChange={setActiveTab} testid="accounting-tabs" />

      {activeTab === 'Summary' && (
        <>
          <Stats items={[
            { label: 'Commissions this year', value: fmtCAD(summary?.commissions_ytd || 0) },
            { label: 'Trust balance', value: fmtCAD(trustBalance), attention: trustBalance < 0 },
            { label: 'Pending payouts', value: fmtCAD(summary?.pending_payouts || 0) },
          ]} />
          <Section title="Commission records" action={<TextLink onClick={() => setActiveTab('Commission Records')}>View all</TextLink>}>
            <List>
              {['pending','approved','paid'].map(status => {
                const rows = commissions.filter(c => c.status === status);
                return (
                  <Row key={status} title={humanize(status)}
                    meta={`${rows.length} record${rows.length !== 1 ? 's' : ''}`}
                    status={money(rows.reduce((t, c) => t + (c.agent_payout || 0), 0))} />
                );
              })}
            </List>
          </Section>
          <Section title="Trust account" action={<TextLink onClick={() => setActiveTab('Trust Account')}>Open ledger</TextLink>}
            note={`Approved balance from ${trustApproved.length} entries${trust.length > trustApproved.length ? `, ${trust.filter(e => e.status === 'pending').length} waiting on the broker of record` : ''}.`}>
            {trustApproved.length === 0 ? <Empty>No approved trust entries yet.</Empty> : (
              <List>
                {trustApproved.slice(0, 4).map((e, i) => (
                  <Row key={e.id || i} title={e.property_address || e.description || 'Entry'}
                    meta={[humanize(e.type || (dirIn(e) ? 'deposit' : 'disbursement')), e.date || e.created_at ? { text: fmtDate(e.date || e.created_at), figure: true } : null]}
                    status={<span style={{ fontFamily:F.mono, fontSize:12.5 }}>{dirIn(e) ? '+' : '−'}{fmtCAD(e.amount)}</span>} />
                ))}
              </List>
            )}
          </Section>
        </>
      )}

      {activeTab === 'Commission Plans' && (
        <>
          <Section title="Commission plans" action={<TextLink onClick={() => setShowNewPlan(true)}>New plan</TextLink>}>
            {plans.length === 0 ? (
              <Empty action={<TextLink onClick={() => setShowNewPlan(true)}>Create a plan</TextLink>}>No commission plans yet.</Empty>
            ) : (
              <List testid="accounting-plans">
                {plans.map(plan => (
                  <Row key={plan.id} testid={`plan-${plan.id}`} title={plan.name}
                    meta={[plan.type === 'percentage' ? 'Percentage split' : plan.type === 'flat' ? 'Flat fee' : humanize(plan.type),
                      { text: plan.type === 'percentage' ? `${plan.rate}% to office` : `${fmtCAD(plan.rate)} per deal`, figure: true }]}
                    next={plan.description || null}
                    status={plan.is_default ? 'Office default' : null}
                    aside={!plan.is_default && isManager ? (
                      <button disabled={busy === `default-${plan.id}`} onClick={() => handleMakeDefault(plan.id)} style={link}>
                        {busy === `default-${plan.id}` ? 'Saving…' : 'Make default'}
                      </button>
                    ) : null} />
                ))}
              </List>
            )}
          </Section>

          <Section title="Agent plans" note="An agent's own plan wins over the office default from its effective date. Changing a plan adds a new assignment, so earlier deals keep the split they closed under.">
            {officeAgents.length === 0 ? <Empty>No agents in this office yet.</Empty> : (
              <List testid="accounting-assignments">
                {officeAgents.map(a => {
                  const cur = currentByAgent[a.agent_id];
                  const draft = assignDraft[a.agent_id] || {};
                  const n = historyCount(a.agent_id);
                  return (
                    <Row key={a.agent_id} title={a.name || a.email || 'Agent'}
                      meta={cur ? [cur.plan_name, { text: `since ${cur.effective_from}`, figure: true }, n > 1 ? `${n} assignments` : null]
                        : [`Office default${defaultPlan ? `, ${defaultPlan.name}` : ''}`]}
                      aside={isManager ? (
                        <span style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', justifyContent:'flex-end' }}>
                          <select aria-label="Plan" value={draft.plan_id || ''} onChange={e => setAssignDraft(prev => ({ ...prev, [a.agent_id]: { ...draft, plan_id:e.target.value } }))} style={{ ...fieldStyle, minWidth:140 }}>
                            <option value="">Change plan…</option>
                            {plans.map(pl => <option key={pl.id} value={pl.id}>{pl.name}</option>)}
                          </select>
                          {draft.plan_id && <input type="date" aria-label="Effective from" value={draft.effective_from || ''} title="Effective from (blank is today)" onChange={e => setAssignDraft(prev => ({ ...prev, [a.agent_id]: { ...draft, effective_from:e.target.value } }))} style={{ ...fieldStyle, fontFamily:F.mono, fontSize:12 }}/>}
                          {draft.plan_id && <button disabled={busy === `assign-${a.agent_id}`} onClick={() => handleAssign(a.agent_id)} style={link}>{busy === `assign-${a.agent_id}` ? 'Assigning…' : 'Assign'}</button>}
                        </span>
                      ) : null} />
                  );
                })}
              </List>
            )}
          </Section>

          <Section title="Agent statements" note="Built from this office's commission records. Payouts are made by your office.">
            <div style={{ display:'flex', gap:10, alignItems:'flex-end', flexWrap:'wrap' }}>
              <div><FieldLabel>Agent</FieldLabel>
                <select value={stmtAgentId || ''} onChange={e => { setStmtForm({ ...stmtForm, agent_id:e.target.value }); setStatement(null); }} disabled={!isManager} style={{ ...fieldStyle, marginTop:6, minWidth:200 }}>
                  {isManager && <option value="">Choose agent…</option>}
                  {statementAgents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.name || a.email || 'Agent'}</option>)}
                </select>
              </div>
              <div><FieldLabel>From</FieldLabel>
                <input type="date" value={stmtForm.from} onChange={e => setStmtForm({ ...stmtForm, from:e.target.value })} style={{ ...fieldStyle, marginTop:6, fontFamily:F.mono, fontSize:12 }}/>
              </div>
              <div><FieldLabel>To</FieldLabel>
                <input type="date" value={stmtForm.to} onChange={e => setStmtForm({ ...stmtForm, to:e.target.value })} style={{ ...fieldStyle, marginTop:6, fontFamily:F.mono, fontSize:12 }}/>
              </div>
              <Btn variant="primary" dark={dark} onClick={handleViewStatement} disabled={busy === 'stmt'} style={{ minHeight:44, padding:'0 16px', fontSize:13 }}>{busy === 'stmt' ? 'Loading…' : 'View'}</Btn>
              <button onClick={handleStatementPdf} disabled={busy === 'pdf'} style={{ ...link, alignSelf:'center' }}>{busy === 'pdf' ? 'Building…' : 'Download PDF'}</button>
            </div>
            {statement && (
              <div style={{ display:'flex', flexDirection:'column', gap:16, marginTop:20 }}>
                <Meta parts={[statement.agent?.name, { text: `${statement.period?.from} to ${statement.period?.to}`, figure: true },
                  statement.current_plan ? `${statement.current_plan.label}${statement.current_plan.source === 'office_default' ? ' (office default)' : ''}` : 'No plan set']} />
                <Stats items={[
                  { label: 'Gross commission', value: fmtCAD(statement.totals?.grand?.gross || 0) },
                  { label: 'Office split', value: fmtCAD(statement.totals?.grand?.brokerage_split || 0) },
                  { label: 'Agent payout', value: fmtCAD(statement.totals?.grand?.agent_payout || 0) },
                ]} />
                <Meta parts={[`${statement.totals?.grand?.count ?? 0} deals`, ...['pending','approved','paid'].map(st => figLabel(`${statement.totals?.by_status?.[st]?.count ?? 0}`, `${st}, ${fmtCAD(statement.totals?.by_status?.[st]?.agent_payout || 0)}`))]} />
                {(statement.lines || []).length === 0 ? <Empty>No commission records in this period.</Empty> : (
                  <List>
                    {statement.lines.map(l => (
                      <Row key={l.id} title={l.address || 'Deal'}
                        meta={[{ text: l.date, figure: true }, figLabel(fmtCAD(l.gross), 'gross'), figLabel(fmtCAD(l.brokerage_split), 'office')]}
                        status={<>{money(l.agent_payout)} · {humanize(l.status || 'pending')}</>} />
                    ))}
                  </List>
                )}
              </div>
            )}
          </Section>
        </>
      )}

      {activeTab === 'Trust Account' && (
        <>
          <div style={{ fontSize:13, color:p.ink3, maxWidth:680, lineHeight:1.5 }}>
            Every entry needs broker-of-record approval. Deposits are tracked to the five-business-day rule, reconciled monthly, and kept for six years, with a year-end pack ready for RECO.
          </div>
          <TrustLedger BASE={BASE} headers={headers} p={p} dark={dark} entries={trust} onChange={load}/>
        </>
      )}

      {activeTab === 'Books' && books && (
        <>
          {books.connection ? <Stats testid="books-stats" items={booksStats(books)} /> : null}
          <BooksSync data={books} p={p} dark={dark} mapOpen={mapOpen} setMapOpen={setMapOpen} call={callBooks} />
        </>
      )}

      {activeTab === 'Commission Records' && (
        commissions.length === 0 ? <Empty>No commission records yet. They appear here as deals close.</Empty> : (
          <List testid="accounting-commissions">
            {commissions.map(c => (
              <Row key={c.id} testid={`commission-${c.id}`} title={c.transaction_address || 'Deal'}
                meta={[c.agent_name, figLabel(fmtCAD(c.gross_commission), 'gross'), figLabel(fmtCAD(c.brokerage_split_amount ?? c.brokerage_split), 'office')]}
                status={<>{money(c.agent_payout)} · {humanize(c.status || 'pending')}</>} attention={isManager && (c.status || 'pending') === 'pending'}
                aside={isManager && c.status === 'pending' ? <button onClick={() => handleCommAction(c.id, 'approve')} style={link}>Approve</button>
                  : isManager && c.status === 'approved' ? <button onClick={() => handleCommAction(c.id, 'pay')} style={link}>Mark paid</button> : null} />
            ))}
          </List>
        )
      )}

      {/* New Commission Plan Modal */}
      <Dialog open={showNewPlan} onOpenChange={setShowNewPlan}>
        <DialogContent style={{ maxWidth:420, borderRadius:2, background:p.bg, border:`1px solid ${p.hair}`, padding:'var(--bx-pad)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily:F.display, fontSize:18, fontWeight:500, color:p.ink }}>New commission plan</DialogTitle>
            <DialogDescription style={{ fontFamily:F.display, fontSize:13, color:p.ink3, marginTop:4 }}>Define a commission structure for your agents.</DialogDescription>
          </DialogHeader>
          <div style={{ display:'flex', flexDirection:'column', gap:16, paddingTop:20 }}>
            <div><FieldLabel>Plan name</FieldLabel>
              <input type="text" value={planForm.name} onChange={e => setPlanForm({ ...planForm, name:e.target.value })} placeholder="e.g. Standard 70/30"
                style={{ marginTop:8, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:'transparent', outline:'none', boxSizing:'border-box' }}/>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div><FieldLabel>Type</FieldLabel>
                <select value={planForm.type} onChange={e => setPlanForm({ ...planForm, type:e.target.value })}
                  style={{ marginTop:8, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:p.bg, outline:'none' }}>
                  {[['percentage','Percentage split'],['flat','Flat fee']].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div><FieldLabel>{planForm.type === 'percentage' ? 'Office share of commission (%)' : 'Office fee per deal (CAD)'}</FieldLabel>
                <input type="number" step={planForm.type === 'percentage' ? '0.1' : '1'} value={planForm.rate} onChange={e => setPlanForm({ ...planForm, rate:e.target.value })} placeholder={planForm.type === 'percentage' ? '30' : '5000'}
                  style={{ marginTop:8, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.mono, color:p.ink, background:'transparent', outline:'none', boxSizing:'border-box' }}/>
              </div>
            </div>
            <div><FieldLabel>Description</FieldLabel>
              <textarea value={planForm.description} onChange={e => setPlanForm({ ...planForm, description:e.target.value })} rows={2} placeholder="Optional description..."
                style={{ marginTop:8, width:'100%', padding:'10px 12px', border:`1px solid ${p.hairS}`, borderRadius:2, fontSize:14, fontFamily:F.display, color:p.ink, background:'transparent', outline:'none', resize:'vertical', boxSizing:'border-box' }}/>
            </div>
          </div>
          <DialogFooter style={{ marginTop:24, gap:10 }}>
            <Btn variant="ghost" dark={dark} onClick={() => setShowNewPlan(false)}>Cancel</Btn>
            <Btn variant="primary" dark={dark} onClick={handleCreatePlan}>Create plan</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Page>
  );
}
